import React, { useEffect, useRef } from 'react';
import { X, GitBranch } from 'lucide-react';

const NetworkMapModal = ({ item, onClose }) => {
  const canvasRef = useRef(null);

  const getHealthColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#eab308';
    if (score >= 30) return '#f97316';
    return '#ef4444';
  };



  useEffect(() => {
    if (!canvasRef.current || !item) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Draw background grid
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 1;
    for (let i = 0; i < width; i += 40) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, height);
      ctx.stroke();
    }
    for (let i = 0; i < height; i += 40) {
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(width, i);
      ctx.stroke();
    }

    // Main entity in center
    const centerX = width / 2;
    const centerY = height / 2;
    const mainRadius = 60;

    // Draw main entity
    ctx.fillStyle = getHealthColor(item.healthScore);
    ctx.shadowColor = getHealthColor(item.healthScore);
    ctx.shadowBlur = 20;
    ctx.beginPath();
    ctx.arc(centerX, centerY, mainRadius, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    // Main entity border
    ctx.strokeStyle = '#00e5ff';
    ctx.lineWidth = 3;
    ctx.stroke();

    // Main entity text
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(item.changeNumber, centerX, centerY - 10);
    ctx.font = '12px Arial';
    ctx.fillText(`Score: ${item.healthScore}`, centerX, centerY + 10);

    // Draw upstream dependencies
    const upstream = item.upstream || [];
    const upstreamAngleStep = (Math.PI * 1.5) / Math.max(upstream.length, 1);
    const upstreamStartAngle = Math.PI * 0.75;

    upstream.forEach((dep, idx) => {
      const angle = upstreamStartAngle + (idx * upstreamAngleStep);
      const x = centerX + Math.cos(angle) * 200;
      const y = centerY + Math.sin(angle) * 150;
      const radius = 40;

      // Draw connection line
      ctx.strokeStyle = '#00e5ff40';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(x, y);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw node
      ctx.fillStyle = '#b24bf3';
      ctx.shadowColor = '#b24bf3';
      ctx.shadowBlur = 15;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;

      // Node border
      ctx.strokeStyle = '#b24bf3';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Node text
      ctx.fillStyle = '#ffffff';
      ctx.font = '10px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const displayName = dep.displayName || dep.type || 'Unknown';
      const truncated = displayName.length > 15 ? displayName.substring(0, 12) + '...' : displayName;
      ctx.fillText(truncated, x, y);
    });

    // Draw downstream dependencies
    const downstream = item.downstream || [];
    const downstreamAngleStep = (Math.PI * 1.5) / Math.max(downstream.length, 1);
    const downstreamStartAngle = Math.PI * 1.75;

    downstream.forEach((dep, idx) => {
      const angle = downstreamStartAngle + (idx * downstreamAngleStep);
      const x = centerX + Math.cos(angle) * 200;
      const y = centerY + Math.sin(angle) * 150;
      const radius = 40;

      // Draw connection line
      ctx.strokeStyle = '#ff6b3540';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(x, y);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw node
      ctx.fillStyle = '#ff6b35';
      ctx.shadowColor = '#ff6b35';
      ctx.shadowBlur = 15;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;

      // Node border
      ctx.strokeStyle = '#ff6b35';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Node text
      ctx.fillStyle = '#ffffff';
      ctx.font = '10px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const displayName = dep.displayName || dep.type || 'Unknown';
      const truncated = displayName.length > 15 ? displayName.substring(0, 12) + '...' : displayName;
      ctx.fillText(truncated, x, y);
    });

  }, [item]);

  if (!item) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
         onClick={onClose}>
      <div className="bg-dark-card border border-orange-accent/40 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden"
           style={{ boxShadow: '0 0 60px rgba(255, 107, 53, 0.3)' }}
           onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-accent/20 to-purple-accent/20 border-b border-orange-accent/30 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-orange-accent mb-1"
                style={{ textShadow: '0 0 20px #ff6b35' }}>
              Network Map: {item.changeNumber}
            </h2>
            <p className="text-gray-400 text-sm">{item.totalRelationships} total relationships</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-orange-accent hover:border-orange-accent/40 transition-all"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Legend */}
          <div className="mb-4 flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-cyan-accent" style={{ boxShadow: '0 0 10px #00e5ff' }}></div>
              <span className="text-gray-400">Main Entity</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-purple-accent" style={{ boxShadow: '0 0 10px #b24bf3' }}></div>
              <span className="text-gray-400">Upstream ({item.upstream?.length || 0})</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-orange-accent" style={{ boxShadow: '0 0 10px #ff6b35' }}></div>
              <span className="text-gray-400">Downstream ({item.downstream?.length || 0})</span>
            </div>
          </div>

          {/* Canvas */}
          <div className="bg-black/40 rounded-lg border border-dark-border/60 overflow-hidden">
            <canvas
              ref={canvasRef}
              width={900}
              height={600}
              className="w-full"
            />
          </div>

          {/* Relationship Details */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Upstream */}
            <div className="bg-dark-border/20 rounded-lg p-4 border border-purple-accent/30">
              <h3 className="text-lg font-semibold text-purple-accent mb-3 flex items-center gap-2">
                <GitBranch size={18} />
                Upstream Dependencies
              </h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {item.upstream && item.upstream.length > 0 ? (
                  item.upstream.map((dep, idx) => (
                    <div key={idx} className="text-sm p-2 bg-dark-border/30 rounded border border-purple-accent/20">
                      <div className="font-semibold text-white">{dep.displayName || 'Unknown'}</div>
                      <div className="text-gray-500 text-xs">{dep.type} • {dep.relationshipType}</div>
                    </div>
                  ))
                ) : (
                  <div className="text-gray-500 text-sm">No upstream dependencies</div>
                )}
              </div>
            </div>

            {/* Downstream */}
            <div className="bg-dark-border/20 rounded-lg p-4 border border-orange-accent/30">
              <h3 className="text-lg font-semibold text-orange-accent mb-3 flex items-center gap-2">
                <GitBranch size={18} />
                Downstream Dependencies
              </h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {item.downstream && item.downstream.length > 0 ? (
                  item.downstream.map((dep, idx) => (
                    <div key={idx} className="text-sm p-2 bg-dark-border/30 rounded border border-orange-accent/20">
                      <div className="font-semibold text-white">{dep.displayName || 'Unknown'}</div>
                      <div className="text-gray-500 text-xs">{dep.type} • {dep.relationshipType}</div>
                    </div>
                  ))
                ) : (
                  <div className="text-gray-500 text-sm">No downstream dependencies</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NetworkMapModal;
